import React, { MouseEventHandler } from 'react';
import { AnimatePresence, motion, MotionProps } from 'framer-motion';
import styled from 'styled-components';
import { Close16 } from '@honeybook/icons';
import { createPortal } from 'react-dom';
import { vars } from '../../theme';
import HBIconButton from '../HBIconButton/HBIconButton';
import { MustIncludeOneOfBasicTestIdWithIdProps } from '../common/internal/test-id.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { useHBUI } from '../HBUIProvider/HBUIProvider';
import { getDocumentBody } from '../../utils/browser.utils';

type HBCornerPopupPlacement = 'left' | 'right';

export interface HBCornerPopupProps {
    /** Controls popup visibility; animates in when true. */
    isOpen: boolean;
    /** Called when the close button is clicked; presence renders the close button. */
    onClose: MouseEventHandler<HTMLButtonElement>;
    /** Content rendered inside the popup container. */
    children: React.ReactNode;
    /** Called when an image element inside children is clicked. */
    onImageClick?: () => void;
    /** Which bottom corner of the viewport the popup anchors to. */
    placement?: HBCornerPopupPlacement;
    /** CSS padding value for the popup container. */
    padding?: string;
    /** Popup width in pixels; constrained between 320px min and 420px max. */
    width?: number;
    /** Z-index of the popup container. @default 999 */
    zIndex?: number;
    /** Inverts the component background color and close IconButton theme for more contrast between the overall UI and the component */
    inverted?: boolean;
}

const HBCornerPopup = ({
    isOpen,
    onClose,
    children,
    placement = 'right',
    padding = '20px',
    testIdName,
    width = 380,
    zIndex = 999, // one less than the modal
    inverted = false,
    ...props
}: HBCornerPopupProps & MustIncludeOneOfBasicTestIdWithIdProps) => {
    const { getCornerPopupPortalTarget } = useHBUI();

    const testId = props.testId ?? props.id;

    const containerTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'container',
        componentName: 'HBCornerPopup'
    });

    const animation = placement === 'right' ? RIGHT_ANIMATION : LEFT_ANIMATION;

    const popup = (
        <AnimatePresence>
            {isOpen && (
                <Container
                    data-testid={containerTestId}
                    $placement={placement}
                    $padding={padding}
                    $width={width}
                    $zIndex={zIndex}
                    $inverted={inverted}
                    {...animation}
                >
                    {children}
                    {onClose && (
                        <CloseButton
                            testId={`${testId}-close`}
                            testIdName={`${testIdName}-close`}
                            icon={Close16}
                            onClick={onClose}
                            size="xs"
                            label="Close"
                            buttonTheme={
                                inverted
                                    ? 'light-invisible'
                                    : 'subtle-invisible'
                            }
                        />
                    )}
                </Container>
            )}
        </AnimatePresence>
    );

    return createPortal(
        popup,
        getCornerPopupPortalTarget() || getDocumentBody()
    );
};

export default HBCornerPopup;

const RIGHT_ANIMATION = createAnimation('right');
const LEFT_ANIMATION = createAnimation('left');

function createAnimation(placement: HBCornerPopupPlacement): MotionProps {
    const x = placement === 'right' ? 20 : -20;

    return {
        transition: { duration: 0.3, ease: [0.8, 0, 0.2, 1] },
        initial: { opacity: 0, x },
        animate: { opacity: 1, x: 0 },
        exit: { opacity: 0, x }
    };
}

const Container = styled(motion.div)<{
    $placement: HBCornerPopupPlacement;
    $padding: string;
    $width: number;
    $zIndex: number;
    $inverted: boolean;
}>`
    border-radius: ${vars.radius.sm};
    background-color: ${props =>
        props.$inverted
            ? vars.colors.surface.itemInverted
            : vars.colors.surface.container};
    box-shadow: ${vars.shadow['40']};
    padding: ${props => props.$padding};
    position: absolute;
    z-index: ${props => props.$zIndex};
    overflow: hidden;
    margin: 24px;
    bottom: 0;
    right: ${props => (props.$placement === 'right' ? '0' : 'auto')};
    left: ${props => (props.$placement === 'left' ? '0' : 'auto')};
    width: ${props => props.$width}px;
    min-width: 320px;
    max-width: 420px;
`;

const CloseButton = styled(HBIconButton)`
    position: absolute;
    top: 8px;
    right: 8px;
`;
